package top.wcpe.allincore.ui.runtime.event

/** 冻结的规范化输入事件种类。 */
enum class InputTraceKind(val wireName: String) {
    POINTER_DOWN("pointerDown"),
    MOVE("move"),
    UP("up"),
    CANCEL("cancel"),
    CLICK("click"),
    DOUBLE_CLICK("doubleClick"),
    DRAG("drag"),
    SCROLL("scroll"),
    KEY("key"),
    CHAR("char"),
    FOCUS("focus"),
}

/**
 * 平台无关的规范化输入 trace 条目。
 *
 * 仅保留逻辑输入字段，不包含时间戳、平台原始键码或字符正文。
 */
data class InputTraceEntry(
    val seq: Long = UNASSIGNED_SEQUENCE,
    val kind: InputTraceKind,
    val targetPath: String = "",
    val x: Double? = null,
    val y: Double? = null,
    val dx: Double? = null,
    val dy: Double? = null,
    val button: Int? = null,
    val buttonName: String? = null,
    val scrollX: Double? = null,
    val scrollY: Double? = null,
    val key: String? = null,
    val action: Int? = null,
    val modifiers: Int? = null,
    val holdMs: Long? = null,
    val mappingVersion: String? = null,
    val codepoint: Int? = null,
    val focusBefore: String? = null,
    val focusAfter: String? = null,
    val handled: Boolean,
    val consumed: Boolean,
) {
    init {
        require(kind == InputTraceKind.KEY || consumed == handled) {
            "非键盘 trace 的 consumed 必须等于 handled"
        }
        require(kind != InputTraceKind.KEY || !consumed || handled) {
            "键盘 trace 已消费时必须同时标记为已处理"
        }
    }

    companion object {
        private const val UNASSIGNED_SEQUENCE: Long = -1L

        /** 创建消费结果投影为 handled 的非键盘条目。 */
        fun nonKey(
            kind: InputTraceKind,
            targetPath: String = "",
            handled: Boolean,
            x: Double? = null,
            y: Double? = null,
            dx: Double? = null,
            dy: Double? = null,
            button: Int? = null,
            buttonName: String? = null,
            scrollX: Double? = null,
            scrollY: Double? = null,
            codepoint: Int? = null,
            focusBefore: String? = null,
            focusAfter: String? = null,
        ): InputTraceEntry {
            require(kind != InputTraceKind.KEY) { "键盘 trace 必须使用 key 工厂" }
            return InputTraceEntry(
                kind = kind,
                targetPath = targetPath,
                x = x,
                y = y,
                dx = dx,
                dy = dy,
                button = button,
                buttonName = buttonName,
                scrollX = scrollX,
                scrollY = scrollY,
                codepoint = codepoint,
                focusBefore = focusBefore,
                focusAfter = focusAfter,
                handled = handled,
                consumed = handled,
            )
        }

        /** 创建保留 handled/consumed 区分的键盘条目。 */
        fun key(
            targetPath: String = "",
            key: String,
            action: Int,
            modifiers: Int,
            holdMs: Long,
            result: KeyDispatchResult,
            mappingVersion: String,
        ): InputTraceEntry = InputTraceEntry(
            kind = InputTraceKind.KEY,
            targetPath = targetPath,
            key = key,
            action = action,
            modifiers = modifiers,
            holdMs = holdMs,
            mappingVersion = mappingVersion,
            handled = result.handled,
            consumed = result.consumed,
        )
    }
}

/** 规范化输入 trace 的最小接收接缝。 */
fun interface InputTraceSink {
    fun record(entry: InputTraceEntry)
}

/** 按接收顺序分配序号并保存当前 session trace。 */
class InputTraceCollector : InputTraceSink {
    private val entries = ArrayList<InputTraceEntry>()

    override fun record(entry: InputTraceEntry) {
        entries.add(entry.copy(seq = entries.size.toLong()))
    }

    fun snapshot(): List<InputTraceEntry> = entries.toList()

    fun clear() {
        entries.clear()
    }
}
